import React, { useState } from "react";
import useDeviceType from "../../hooks/useDeviceType";
import { BRANDING } from "../../constants/branding";

interface HelpButtonProps {}

const HelpButton: React.FC<HelpButtonProps> = () => {
	const [isOpen, setIsOpen] = useState(false);
	const { isMobile } = useDeviceType();

	const keyboardShortcuts = [
		{
			title: "Run Code",
			content: "Execute your Python code",
			shortcut: "⌘/Ctrl + Enter",
			icon: (
				<svg
					className="w-5 h-5"
					fill="currentColor"
					stroke="none"
					viewBox="0 0 24 24"
				>
					<path d="M8 5v14l11-7z" />
				</svg>
			),
		},
		{
			title: "Alternative Run",
			content: "Another way to run code (when not debugging)",
			shortcut: "⌘/Ctrl + R",
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
					/>
				</svg>
			),
		},
		{
			title: "Learn Mode / Step Forward",
			content: "Start debugging or step to next line",
			shortcut: "⌘/Ctrl + D or F9",
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
					></path>
				</svg>
			),
		},
		{
			title: "Step Forward",
			content: "Execute next line while debugging",
			shortcut: "F10",
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M8.25 4.5l7.5 7.5-7.5 7.5"
					/>
				</svg>
			),
		},
		{
			title: "Step Backward",
			content: "Go back one step while debugging",
			shortcut: "Shift + F10",
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M15.75 19.5L8.25 12l7.5-7.5"
					/>
				</svg>
			),
		},
		{
			title: "Fullscreen Mode",
			content: "Toggle fullscreen view like YouTube",
			shortcut: "F11 or F",
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M4 8V4m0 0h4m-4 0l5.657 5.657M20 8V4m0 0h-4m4 0l-5.657 5.657M4 16v4m0 0h4m-4 0l5.657-5.657M20 16v4m0 0h-4m4 0l-5.657-5.657"
					/>
				</svg>
			),
		},
		{
			title: "Exit Learn Mode",
			content: "Stop debugging and return to coding",
			shortcut: "Escape",
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			),
		},
	];

	return (
		<>
			{/* Help Button */}
			<div
				className={`fixed z-40 ${isMobile ? "bottom-4 right-4" : "bottom-8 right-8"}`}
			>
				<button
					onClick={() => setIsOpen(!isOpen)}
					className={`${BRANDING.colors.backgrounds.primary} hover:bg-slate-600 text-white rounded-full ${isMobile ? "w-12 h-12" : "w-16 h-16"} shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center group transform hover:scale-105 active:scale-95 border-2 border-slate-400/30 hover:border-slate-300/50`}
					title="Need help? Click here!"
				>
					<div className="relative">
						<svg
							className={`${isMobile ? "w-5 h-5" : "w-7 h-7"} transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							{isOpen ? (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							) : isMobile ? (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							) : (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M4 8a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V8zm3 2h.01m3.99 0h.01m3.99 0h.01M7 14h10"
								/>
							)}
						</svg>
					</div>
				</button>
			</div>

			{/* Help Panel */}
			{isOpen && (
				<div
					className={`fixed z-40 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 border border-gray-200 dark:border-gray-700 transform animate-in slide-in-from-bottom-4 backdrop-blur-sm bg-white/95 dark:bg-gray-800/95 overflow-y-auto ${
						isMobile
							? "inset-x-4 bottom-20 max-h-[70vh] max-w-sm"
							: "bottom-32 right-8 w-96 max-h-none"
					}`}
				>
					{/* Header */}
					<div
						className={`${BRANDING.colors.backgrounds.primary} text-white ${isMobile ? "p-4" : "p-5"} border-b border-slate-400/20`}
					>
						<h3
							className={`font-bold ${isMobile ? "text-base" : "text-lg"} flex items-center gap-2`}
						>
							<div
								className={`${isMobile ? "w-5 h-5" : "w-6 h-6"} bg-white/20 rounded-lg flex items-center justify-center`}
							>
								<svg
									className={`${isMobile ? "w-3 h-3" : "w-4 h-4"}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
									></path>
								</svg>
							</div>
							{isMobile ? "Quick Actions" : "Keyboard Shortcuts"}
						</h3>
						<p
							className={`text-blue-100 ${isMobile ? "text-xs" : "text-sm"} mt-1 font-medium`}
						>
							{isMobile
								? "Get started with Python"
								: "Speed up your coding workflow"}
						</p>
					</div>

					{/* Content - Hide keyboard shortcuts on mobile */}
					{!isMobile && (
						<div className="p-5 space-y-1.5">
							{keyboardShortcuts.map((shortcut, index) => (
								<div
									key={index}
									className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-all duration-200 group"
								>
									<div className="flex items-center gap-3">
										<div className="w-5 h-5 flex items-center justify-center text-slate-500 dark:text-slate-400 flex-shrink-0">
											{shortcut.icon}
										</div>
										<span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
											{shortcut.title}
										</span>
									</div>
									<kbd className="px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-md border border-gray-200 dark:border-gray-600 font-mono shadow-sm flex-shrink-0">
										{shortcut.shortcut}
									</kbd>
								</div>
							))}
						</div>
					)}

					{/* Quick Actions */}
					<div
						className={`border-t border-gray-200 dark:border-gray-700 ${isMobile ? "p-4" : "p-5"} bg-gray-50/50 dark:bg-gray-750/30`}
					>
						<div className={`${isMobile ? "space-y-2" : "space-y-3"}`}>
							<div className="text-center">
								<p
									className={`text-gray-600 dark:text-gray-400 ${isMobile ? "text-xs" : "text-sm"}`}
								>
									Use keyboard shortcuts to speed up your coding workflow
								</p>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Backdrop */}
			{isOpen && (
				<div
					className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 transition-all duration-300"
					onClick={() => setIsOpen(false)}
				/>
			)}
		</>
	);
};

export default HelpButton;
